import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/common/SEOHead';
import './FAQPage.css';

const ChevronDown = () => (
  <svg className="faq-caret" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

const SearchIcon = () => (
  <svg className="faq-search-icon" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
  </svg>
);

// FAQ data organized by category
const FAQ_DATA = [
  {
    category: 'About The Orbital Ledger',
    items: [
      {
        q: 'Is The Orbital Ledger free?',
        a: (
          <>
            <p>
              All game data is completely free: items, ships, prices, minerals,
              locations, routes, and community tools like CrewLink. You never need
              to pay to browse or contribute data.
            </p>
            <p>
              Premium plans cover cloud storage for your personal data, including inventory
              tracking, saved loadouts, goal progress, and wallet history synced
              across devices. Think of it like Google Drive for your Star Citizen
              career. It starts with a free 14-day trial, no credit card required.
            </p>
            <p>
              Orgs are free to create with unlimited members. Edit seats for
              officers who need to modify org data are available through org
              subscriptions.
            </p>
          </>
        ),
      },
      {
        q: 'Why is there a subscription?',
        a: (
          <>
            <p>
              Everything those free tools offer IS free here too: item stats, ship
              specs, commodity prices, trade routes, mining locations. We don't
              charge for game data.
            </p>
            <p>
              The subscription covers a different thing entirely: storing YOUR
              data on our servers. Your personal inventory, your org's fleet
              tracker, your wallet history, your saved loadouts. That's data
              we host, sync, and back up for you. That costs us real server
              resources, which the subscription covers.
            </p>
            <p>
              If you just want to look up where to buy a P4-AR or compare
              refinery yields, you'll never see a paywall.
            </p>
          </>
        ),
      },
      {
        q: 'What is The Orbital Ledger?',
        a: (
          <>
            <p>
              The Orbital Ledger (TOL) is a free, community-driven companion app for
              Star Citizen. We provide tools that help players track commodity prices,
              plan trade routes, browse ship and equipment databases, manage personal
              inventory, run organizations, and connect with other citizens.
            </p>
            <p>
              TOL is <strong>not affiliated with Cloud Imperium Games</strong> (CIG) or
              Roberts Space Industries (RSI). It is an independent fan project built by
              citizens, for citizens.
            </p>
          </>
        ),
      },
      {
        q: 'Is this affiliated with Cloud Imperium Games (CIG)?',
        a: (
          <>
            <p>
              <strong>No.</strong> The Orbital Ledger is an unofficial, independent fan site.
              We are not affiliated with, endorsed by, or sponsored by Cloud Imperium Games,
              Roberts Space Industries, or any of their subsidiaries.
            </p>
            <p>
              All Star Citizen game data presented on TOL (ship specs, item stats, locations,
              commodity prices) is publicly available information sourced from the game and
              community resources. We do not sell or monetize game data. It is provided free
              of charge to all visitors.
            </p>
            <p>
              Star Citizen®, Squadron 42®, Roberts Space Industries®, and Cloud Imperium® are
              registered trademarks of Cloud Imperium Rights LLC.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Free Features & Game Data',
    items: [
      {
        q: 'What can I access for free without signing in?',
        a: (
          <>
            <p>
              All Star Citizen game data on TOL is <strong>completely free and requires no
              account</strong>. You can browse every page listed below without signing in:
            </p>
            <div className="faq-feature-grid">
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Ship Matrix <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  Browse all Star Citizen ships with detailed specs, manufacturer info,
                  cargo capacity, roles, and variants.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Equipment Database <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  Explore 1,700+ items across armor, weapons, ship components, commodities,
                  and miscellaneous gear with stats and locations.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Commodity Prices <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  Real-time, community-submitted commodity prices with historical charts,
                  trends, and market analysis.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Charts & Analytics <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  Interactive price charts with technical indicators, volume data, and
                  market dashboards.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Locations <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  Complete database of systems, planets, moons, stations, and landing zones
                  with merchant and item availability info.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Route Planner <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  Plan trade routes between locations with profit calculations and cargo
                  optimization.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Leaderboards <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  Community leaderboards for price submissions, item voting, and player
                  contributions.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Public Profiles <span className="faq-feature-card-badge faq-badge-free">Free</span>
                </div>
                <div className="faq-feature-card-desc">
                  View other players' public profiles, badges, and contribution stats.
                </div>
              </div>
            </div>
            <p>
              <strong>We believe game data should always be free.</strong> You will never need
              to pay to look up ship specs, check commodity prices, or browse equipment stats.
            </p>
          </>
        ),
      },
      {
        q: 'What requires a free account (sign-in only)?',
        a: (
          <>
            <p>
              A few community features require a <strong>free account</strong> (no subscription
              needed) for moderation and trust purposes:
            </p>
            <div className="faq-feature-grid">
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  The Exchange <span className="faq-feature-card-badge faq-badge-account">Free Account</span>
                </div>
                <div className="faq-feature-card-desc">
                  Player-to-player marketplace for trading in-game items and services.
                  Requires sign-in so we can moderate listings and protect users from scams.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  CrewLink <span className="faq-feature-card-badge faq-badge-account">Free Account</span>
                </div>
                <div className="faq-feature-card-desc">
                  Looking-for-group board to find crews, org recruits, and mission partners.
                  Requires sign-in for accountability and to prevent spam.
                </div>
              </div>
              <div className="faq-feature-card">
                <div className="faq-feature-card-name">
                  Price Submissions <span className="faq-feature-card-badge faq-badge-account">Free Account</span>
                </div>
                <div className="faq-feature-card-desc">
                  Submit commodity prices you find in-game to help keep the database accurate.
                  Requires sign-in to track contribution quality.
                </div>
              </div>
            </div>
            <p>
              Creating an account is free and always will be. These features require sign-in
              purely for moderation, not as a paywall.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Subscriptions & Pricing',
    items: [
      {
        q: 'Why do some features require a paid subscription?',
        a: (
          <>
            <p>
              <strong>The short answer: server costs.</strong>
            </p>
            <p>
              All Star Citizen game data (ships, items, prices, locations) is publicly available
              and free on TOL. We do not charge for access to game information. However, features
              like personal inventory tracking, wallet management, goal planning, ship hangars, and
              organization management require us to store <em>your</em> data on our servers.
            </p>
            <p>
              Hosting, database storage, and infrastructure cost real money to operate. Subscription
              fees directly fund these server costs so we can keep the lights on and continue
              providing this service to the community.
            </p>
            <p>
              <strong>To put it simply:</strong> game data is free. Storing your personal data
              (inventory, goals, org info) on our servers requires a subscription to cover
              hosting costs. We think that's a fair trade.
            </p>
          </>
        ),
      },
      {
        q: 'What does my personal subscription pay for?',
        a: (
          <>
            <p>
              A Ledger Pro subscription unlocks tools that store and manage your personal data:
            </p>
            <ul>
              <li><strong>Personal Inventory</strong> — Track every item you own in-game across locations</li>
              <li><strong>Wallet & Transactions</strong> — Log aUEC income, expenses, and net worth over time</li>
              <li><strong>Ship Hangar</strong> — Manage your fleet with acquisition goals and status tracking</li>
              <li><strong>Wishlist</strong> — Track items you want with location-grouped shopping lists</li>
              <li><strong>Gear Loadouts</strong> — Build and save equipment loadouts for different activities</li>
              <li><strong>Budget Manager</strong> — Plan savings toward ships and gear purchases</li>
              <li><strong>Trade History</strong> — Log and analyze your trading performance</li>
            </ul>
            <p>
              All of this data is stored on our servers and syncs across your devices. The
              subscription covers the infrastructure required to keep it available to you.
            </p>
          </>
        ),
      },
      {
        q: 'What are Organization subscriptions?',
        a: (
          <>
            <p>
              Organization subscriptions are separate from personal subscriptions and are designed
              for org leaders who need tools to manage their group. Tiers differ by the number
              of <strong>Command Seats</strong>, users who can edit org data, manage inventory,
              assign cargo, and perform admin tasks.
            </p>
            <ul>
              <li><strong>Free</strong> — 1 command seat (leader only), basic dashboard, cargo tracking</li>
              <li><strong>Squadron ($9.99/mo)</strong> — 10 command seats, events & scheduling, fleet view</li>
              <li><strong>Fleet ($24.99/mo)</strong> — 25 command seats, org analytics, points reward system</li>
              <li><strong>Armada ($49.99/mo)</strong> — 50 command seats, everything in Fleet</li>
            </ul>
            <p>
              <strong>All plans allow unlimited members</strong> to join and view dashboards, participate
              in events, and access org resources. Command Seats grant elevated permissions only.
            </p>
          </>
        ),
      },
      {
        q: 'Can I cancel my subscription anytime?',
        a: (
          <>
            <p>
              <strong>Yes.</strong> You can cancel at any time from your profile's Subscriptions tab
              or through the Stripe customer portal. Your subscription benefits continue through the
              end of your current billing period. You won't lose access immediately.
            </p>
            <p>
              After your subscription expires, your data is retained for a grace period in case you
              resubscribe. See our <Link to="/terms">Terms of Service</Link> and{' '}
              <Link to="/privacy">Privacy Policy</Link> for details on data retention.
            </p>
          </>
        ),
      },
      {
        q: 'Can I gift a subscription to someone?',
        a: (
          <>
            <p>
              <strong>Yes!</strong> You can gift Org Command subscriptions to support your org leader
              or fellow members. Gift subscriptions are available in 1 to 12 month increments across
              all org tiers (Squadron, Fleet, and Armada).
            </p>
            <ul>
              <li>Gifts can be sent <strong>anonymously</strong> if you prefer</li>
              <li>You can include a <strong>personal message</strong> with your gift</li>
              <li>The org leader chooses whether to <strong>extend</strong> their current plan or <strong>upgrade</strong> to the gifted tier</li>
            </ul>
            <p>
              Gift subscriptions are a great way to support org leaders who put in the work to keep
              things running. When a community chips in, everyone benefits.
            </p>
            <p style={{ fontSize: '0.9rem', opacity: 0.85 }}>
              <strong>Note:</strong> Refunds for gifted subscriptions are issued to the original
              purchaser, not the recipient. If a gift is refunded, the recipient's remaining gifted
              time will be removed. See our <Link to="/terms#gift-subscriptions">Terms of Service</Link> for
              full details.
            </p>
          </>
        ),
      },
      {
        q: 'What is your refund policy?',
        a: (
          <>
            <p>
              We want to be fair and transparent about refunds:
            </p>
            <ul>
              <li>
                <strong>14-day free trial</strong> — Try premium features for free with no credit card
                required. This is your risk-free way to decide if a subscription is right for you.
              </li>
              <li>
                <strong>Prorated refunds</strong> — If you request a refund, it will be prorated based
                on the unused portion of your current billing period. For example, if you request a refund
                on day 20 of a 30-day cycle, you would receive a refund for the remaining 10 days.
              </li>
              <li>
                <strong>Refund window</strong> — Refund requests must be submitted within 14 days of
                the charge in question.
              </li>
              <li>
                <strong>Gift subscriptions</strong> — Refunds for gifts are issued to the person who
                purchased the gift, not the recipient. The recipient's gifted time is adjusted accordingly.
              </li>
            </ul>
            <p>
              To request a refund, contact us at{' '}
              <a href="mailto:support@theorbitalledger.com">support@theorbitalledger.com</a> with your
              account email and the reason for your request. We aim to process all refund requests
              within 5 business days.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Rules & Policies',
    items: [
      {
        q: 'Are real-money transactions (RMT) allowed?',
        a: (
          <>
            <div className="faq-callout-warning">
              <p>
                <strong>No. Real-money trading is strictly prohibited on The Orbital Ledger.</strong>
              </p>
            </div>
            <p>
              You may not use any TOL feature, including the marketplace, direct messages,
              organization tools, or any listing or communication channel, to facilitate, solicit,
              advertise, or conduct transactions involving real currency in exchange for in-game items,
              currency, ships, or services.
            </p>
            <p>
              <strong>What IS allowed:</strong>
            </p>
            <ul>
              <li><strong>aUEC transactions</strong> — Buy and sell using in-game currency</li>
              <li><strong>Item-for-item trades</strong> — Barter in-game items directly (e.g., trade
                a rare helmet for a weapon of equal value)</li>
              <li><strong>Any combination of in-game assets</strong> — aUEC + items, items for items,
                or straight aUEC — as long as everything exchanged exists entirely within the game</li>
            </ul>
            <p>
              <strong>What is NOT allowed:</strong>
            </p>
            <ul>
              <li>Real money (USD, EUR, or any real currency) for in-game items or services</li>
              <li>Cryptocurrency, gift cards, or any real-world value in exchange for in-game assets</li>
              <li>Soliciting or advertising RMT through any TOL channel</li>
            </ul>
            <p>
              RMT violations result in <strong>immediate permanent account termination</strong> and
              may be reported to Cloud Imperium Games. This policy exists to protect our community
              and comply with CIG's Terms of Service. For full details, see{' '}
              <Link to="/terms#acceptable-use">Section 5 of our Terms of Service</Link>.
            </p>
          </>
        ),
      },
      {
        q: 'Where can I find the full Terms of Service and Privacy Policy?',
        a: (
          <>
            <p>
              You can read our complete legal documents at any time:
            </p>
            <ul>
              <li><Link to="/terms">Terms of Service</Link> — Rules governing use of the platform</li>
              <li><Link to="/privacy">Privacy Policy</Link> — How we collect, use, and protect your data</li>
              <li><Link to="/community-rules">Community Rules</Link> — Guidelines for community interactions</li>
            </ul>
          </>
        ),
      },
    ],
  },
  {
    category: 'Your Data & Privacy',
    items: [
      {
        q: 'Is my personal data safe?',
        a: (
          <>
            <p>
              Yes. Your account is protected by industry-standard encryption and authentication,
              with optional two-factor authentication. Row-level security ensures you can only access
              your own data. Payment processing is handled entirely by Stripe. We never see or store
              your credit card details.
            </p>
            <p>
              For complete details on what data we collect and how it's used, see
              our <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </>
        ),
      },
      {
        q: 'Can I delete my account and data?',
        a: (
          <>
            <p>
              Yes. You can request full account deletion from your profile settings under
              Account Management. This removes your profile, inventory, wallet data, org memberships,
              and all associated information from our servers. Active subscriptions are cancelled
              through Stripe as part of the deletion process.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Troubleshooting',
    items: [
      {
        q: 'DMs, notifications, or real-time features are not working. What do I do?',
        a: (
          <>
            <p>
              The Orbital Ledger uses <strong>WebSocket connections</strong> to deliver DMs, notifications,
              CrewLink alerts, and Exchange updates in real time. Some browser settings, extensions, or
              network configurations can block these connections, causing live features to silently stop
              working.
            </p>
            <div className="faq-callout-warning">
              <p>
                <strong>Most common fix:</strong> Whitelist <code>theorbitalledger.com</code> and{' '}
                <code>supabase.co</code> in your ad blocker or privacy extension.
              </p>
            </div>
            <p><strong>Common causes and fixes:</strong></p>
            <ul>
              <li>
                <strong>Ad blockers</strong> (uBlock Origin, AdGuard, etc.) — These may classify our
                database connections as "third-party trackers" and block them. Add{' '}
                <code>theorbitalledger.com</code> and <code>*.supabase.co</code> to your allowlist.
              </li>
              <li>
                <strong>Privacy extensions</strong> (Privacy Badger, DuckDuckGo Privacy Essentials,
                Ghostery) — Similar to ad blockers, these may block the WebSocket connections needed
                for live updates. Disable them for this site or add it to the trusted list.
              </li>
              <li>
                <strong>Firefox Enhanced Tracking Protection</strong> — If set to "Strict" mode,
                Firefox may block cross-origin connections. Go to Settings &gt; Privacy &amp; Security
                and either switch to "Standard" or add an exception for theorbitalledger.com.
              </li>
              <li>
                <strong>Safari Intelligent Tracking Prevention</strong> — Safari aggressively blocks
                cross-site connections. Go to Safari &gt; Settings &gt; Privacy and uncheck "Prevent
                cross-site tracking", or use the site in Chrome/Firefox/Edge instead.
              </li>
              <li>
                <strong>Brave browser shields</strong> — Brave blocks third-party connections by
                default. Click the Brave shield icon in the address bar and lower shields for this site.
              </li>
              <li>
                <strong>Corporate or school networks</strong> — Some network firewalls block WebSocket
                connections. Try using a personal network or VPN.
              </li>
            </ul>
            <p>
              <strong>How to test:</strong> After making changes, refresh the page. If you see a
              warning banner about "Some background features are limited" above the page content, the connection is
              still blocked. If the banner disappears, real-time features are working.
            </p>
          </>
        ),
      },
      {
        q: 'What are cookies used for on this site?',
        a: (
          <>
            <p>
              We use two types of cookies:
            </p>
            <ul>
              <li>
                <strong>Essential cookies</strong> — Required to keep you signed in and ensure the
                site works properly. These cannot be disabled without breaking core functionality.
              </li>
              <li>
                <strong>Analytics cookies</strong> — Optional cookies that help us understand how
                people use the site so we can improve it. You can accept or decline these via the
                cookie consent banner.
              </li>
            </ul>
            <p>
              <strong>Important:</strong> DMs, notifications, and real-time features do <em>not</em>{' '}
              use cookies. They use WebSocket connections (a separate technology). If DMs are not
              working, the cookie consent banner is not the issue. See the question above about
              ad blockers and privacy extensions.
            </p>
          </>
        ),
      },
    ],
  },
  {
    category: 'Getting Started',
    items: [
      {
        q: 'How do I get started?',
        a: (
          <>
            <p>
              You can start using TOL right now, no account required. Here's how:
            </p>
            <ul>
              <li><strong>Browse game data</strong> — Check out the <Link to="/ships">Ship Matrix</Link>,{' '}
                <Link to="/items">Equipment Database</Link>, <Link to="/market-overview">Commodity Prices</Link>,
                or <Link to="/locations">Locations</Link> page.</li>
              <li><strong>Plan a trade route</strong> — Use the <Link to="/route-planner">Route Planner</Link> to
                find profitable cargo runs.</li>
              <li><strong>Create a free account</strong> — Sign up to submit prices, use the marketplace,
                and join organizations.</li>
              <li><strong>Subscribe to Ledger Pro</strong> — Unlock personal inventory tracking, wallet
                management, and all premium tools.</li>
            </ul>
          </>
        ),
      },
      {
        q: 'I found a bug or have a feature request. How do I report it?',
        a: (
          <>
            <p>
              We appreciate all feedback from the community. You can reach us at{' '}
              <a href="mailto:support@theorbitalledger.com">support@theorbitalledger.com</a> for
              bug reports, feature requests, or general questions.
            </p>
          </>
        ),
      },
    ],
  },
];

const FAQItem = ({ item, isOpen, onToggle }) => (
  <div className={`faq-item${isOpen ? ' faq-item-open' : ''}`}>
    <button className="faq-question" onClick={onToggle} aria-expanded={isOpen}>
      <span>{item.q}</span>
      <ChevronDown />
    </button>
    {isOpen && <div className="faq-answer">{item.a}</div>}
  </div>
);

const FAQPage = () => {
  const [search, setSearch] = useState('');
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (key) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredData = useMemo(() => {
    if (!search.trim()) return FAQ_DATA;

    const terms = search.toLowerCase().split(/\s+/).filter(Boolean);

    return FAQ_DATA.map((cat) => ({
      ...cat,
      items: cat.items.filter((item) => {
        const text = item.q.toLowerCase();
        return terms.every((t) => text.includes(t));
      }),
    })).filter((cat) => cat.items.length > 0);
  }, [search]);

  return (
    <div className="faq-page">
      <SEOHead
        title="FAQ"
        description="Frequently asked questions about The Orbital Ledger: pricing, features, subscriptions, data privacy, and troubleshooting."
        path="/faq"
      />
      <div className="faq-header">
        <h1 className="faq-title">Frequently Asked Questions</h1>
        <p className="faq-subtitle">Everything you need to know about The Orbital Ledger.</p>
      </div>

      <div className="faq-search-container">
        <SearchIcon />
        <input
          className="faq-search"
          type="text"
          placeholder="Search questions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {filteredData.length === 0 && (
        <div className="faq-no-results">
          No questions match your search. Try different keywords or{' '}
          <a href="mailto:support@theorbitalledger.com">contact us</a>.
        </div>
      )}

      {filteredData.map((cat) => (
        <div key={cat.category} className="faq-category">
          <h2 className="faq-category-title">{cat.category}</h2>
          {cat.items.map((item) => {
            const key = `${cat.category}-${item.q}`;
            return (
              <FAQItem
                key={key}
                item={item}
                isOpen={!!openItems[key]}
                onToggle={() => toggleItem(key)}
              />
            );
          })}
        </div>
      ))}

      <div className="faq-contact">
        <h3>Still have questions?</h3>
        <p>
          Visit our <Link to="/support">Support page</Link> to submit a request, or reach out at{' '}
          <a href="mailto:support@theorbitalledger.com">support@theorbitalledger.com</a>
        </p>
      </div>
    </div>
  );
};

export default FAQPage;
